import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import './BookingForm.css';

import { getMonthsBetween } from '../../utils/dateUtils'; // Adjust the path as necessary
import { apiFetch } from '../../utils/apiClient';
import AvailabilityForm from './AvailabilityForm';
import AvailabilitySummary from './AvailabilitySummary';
import BookingConfirmationTable from '../bookingconfirmation/BookingConfirmationTable';
import PersonalInfoForm from './PersonalInfoForm';
import PayPalButton from "../paypal/PayPalButton";
import BookingCountdown from './CountDown';

const BookingForm = () => {

    // local development uses env.development, production routes to /api
    const API_URL = process.env.REACT_APP_API_URL || "/api";

    // Get URL parameters for discount code
    const [searchParams] = useSearchParams();

    // "daily" or "monthly"
    const [bookingType, setBookingType] = useState('');

    // booking_dates store the selected dates from the date picker/calendar
    // if a daily booking, then these are the actual days
    // if a monthly booking, then these are the first day of each month
    // these are the DateObject of the data picker
    const [bookingDates, setBookingDates] = useState([]);

    // these store the user's selected days and months
    // for daily bookings, selected_desks_daily will store the selected desks for each day
    // for monthly bookings, selected_desks_monthly will store the first day of each month
    // these are formatted dates in the format "YYYY-MM-DD"
    const [finalDesks, setFinalDesks] = useState({});

    // total price calculated from the selected desks
    // updates when the user selects a desk
    const [totalPrice, setTotalPrice] = useState(0);

    // discount code state
    const [discountCode, setDiscountCode] = useState(null);
    const [discountPercentage, setDiscountPercentage] = useState(null);
    const [discountAmount, setDiscountAmount] = useState(null);
    const [originalPrice, setOriginalPrice] = useState(0);

    // renewal flag - allows existing renters to bypass the rolling hold
    const [isRenewal, setIsRenewal] = useState(false);

    const [personalInfo, setPersonalInfo] = useState({
        first_name: '',
        last_name: '',
        email: '',
        // #272. Never sent to the backend -- it exists only so the address is typed twice.
        // It lives in this object rather than inside PersonalInfoForm so that editing it
        // releases a held booking like every other personal detail (see the effect below):
        // changing it means the email is in doubt, and a hold placed under the old one
        // should not survive that.
        confirm_email: '',
        termsAccepted: false,
    });

    const [isBookingDatesValid, setIsBookingDatesValid] = useState(false);
    const [triggerBookingDatesValidation, setTriggerBookingDatesValidation] = useState(false);

    const [isShowAvailabilityPanel, setIsShowAvailabilityPanel] = useState(false);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [isShowPayPalPanel, setIsShowPayPalPanel] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double-clicks

    // group_booking_id is the booking ID returned from the backend API
    // it is the idea that identifies the booking and groups together
    // all days/desks in a single booking
    // this is used to identify the booking when the user makes a payment
    const [group_booking_id, setGroupBookingID] = useState(null);

    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const [apiErrorMessage, setApiErrorMessage] = useState('');

    // Create a reference for smooth transition to the booking calendar
    const calendarRef = useRef(null); // Create a reference for the booking calendar div
    const deskRef = useRef(null); // Create a reference for the desk div
    const summaryRef = useRef(null); // Create a reference for the desk div

    // Function to scroll to the booking-calendar div
    const scrollToCalendar = () => {
        if (calendarRef.current) { // Check if in mobile view
            setTimeout(() => {
                calendarRef.current.scrollIntoView({ behavior: 'smooth' });
            }, 250); // Delay to ensure DOM updates
        }
    };

    // Function to scroll to the booking-calendar div
    const scrollToDesks = () => {
        if (deskRef.current) { // Check if in mobile view
            setTimeout(() => {
                deskRef.current.scrollIntoView({ behavior: 'smooth' });
            }, 250); // Delay to ensure DOM updates
        }
    };

    // Function to scroll to the booking-calendar div
    const scrollToSummary = () => {
        if (summaryRef.current) { // Check if in mobile view
            setTimeout(() => {
                summaryRef.current.scrollIntoView({ behavior: 'smooth' });
            }, 250); // Delay to ensure DOM updates
        }
    };


    const delete_group_booking = async (group_booking_id) => {

        try {

            const bookingsURL = `${API_URL}/bookings/${group_booking_id}`;

            const response = await apiFetch(bookingsURL, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(`${data.error}`);
            }

        } catch (error) {
            // catch any errors, i.e. ones not caught by the fetch call
            // and also the one thrown by the fetch call
            setApiErrorMessage(error.message);
            setIsErrorModalOpen(true);
        }
    };

    const ResetAfterDailyMonthlySelectionChange = () => {

        // this is like a full reset of the booking form
        // when the user changes the booking type, we need to reset everything

        // Reset submitting state so button is re-enabled
        setIsSubmitting(false);

        if (bookingDates.length > 0) {
            setBookingDates([]);
        }

        if (Object.keys(finalDesks).length > 0) {
            setFinalDesks({});
        }

        if (totalPrice) {
            setTotalPrice(0);
        }

        if (isBookingDatesValid) {
            setIsBookingDatesValid(false);
        }

        if (group_booking_id) {

            // call API to delete booking
            delete_group_booking(group_booking_id)

            setIsProcessingPayment(false);
            setGroupBookingID(null);
        }

        scrollToCalendar();

    }

    const ResetAfterDatesChange = () => {

        // we don't need to reset the booking type, just the dates
        // when the user changes the dates, we need to reset the desks
        // and the total price

        // Reset submitting state so button is re-enabled
        setIsSubmitting(false);

        if (Object.keys(finalDesks).length > 0) {
            setFinalDesks({});
        }

        if (totalPrice) {
            setTotalPrice(0);
        }

        if (isBookingDatesValid) {
            setIsBookingDatesValid(false);
        }

        if (group_booking_id) {

            // call API to delete booking
            delete_group_booking(group_booking_id)

            setIsProcessingPayment(false);
            setGroupBookingID(null);
        }

        scrollToDesks();

    }

    const ResetAfterDeskSelectChange = () => {

        // when the user changes the desk selection, we only need
        // to reset the booking object in the database
        // and get a new group_booking_id for their new desk selection

        // Reset submitting state so button is re-enabled
        setIsSubmitting(false);

        if (group_booking_id) {

            // call API to delete booking
            delete_group_booking(group_booking_id)

            setIsProcessingPayment(false);
            setGroupBookingID(null);
        }
    }

    const ResetAfterPersonalInfoChange = () => {

        // Reset submitting state so button is re-enabled
        setIsSubmitting(false);

        if (group_booking_id) {

            // call API to delete booking
            delete_group_booking(group_booking_id)

            setIsProcessingPayment(false);
            setGroupBookingID(null);
        }
    }

    const resetAfterTimeOutOrError = () => {

        // like swithcing the booking type, we need to reset the booking details
        // if the user has not completed the booking in the time allowed
        // this is like a full reset of the booking form

        if (bookingDates.length > 0) {
            setBookingDates([]);
        }

        if (Object.keys(finalDesks).length > 0) {
            setFinalDesks({});
        }

        if (totalPrice) {
            setTotalPrice(0);
        }

        if (isBookingDatesValid) {
            setIsBookingDatesValid(false);
        }

        // Reset submitting state
        setIsSubmitting(false);

        if (group_booking_id) {

            // call API to delete booking
            delete_group_booking(group_booking_id)

            setIsProcessingPayment(false);
            setGroupBookingID(null);
        }

        scrollToCalendar();

    }

    // handles the booking type button clicks, ether daily or monthly
    const handleBookingTypeButtonClick = (bookingType) => {

        // reset the booking details
        setBookingType(bookingType);

        // reset all the other variables
        ResetAfterDailyMonthlySelectionChange();

    };

    // handles the daily date selection
    const handleDailySelect = (dates) => {

        // it is important that the dates are formatted as "YYYY-MM-DD"
        // this ensures that the dates are captured correctly as the user selects them
        // amd they are not converted to UTC. We need to capture the date the user sees/selects
        setBookingDates(dates.map((date) => date.format("YYYY-MM-DD")));
        setBookingType("daily");

        // trigger validation of the dates
        setTriggerBookingDatesValidation(true);

        // reset the other variables, user must reselect the desks
        ResetAfterDatesChange();

        // scroll to the desks
        scrollToDesks();

    };

    // handles the monthly date selection
    const handleMonthlySelect = (months) => {

        // Note the data picker/calendar sends a first and last month if the user select a ranges of months
        // not the full list of months between the start and end
        // so we need to get the full list of months between the start and end

        let list_of_months = [];

        if (months.length === 1) {
            // if length of booking_months is 1, then we just have a single month
            // just need the first day of the month
            list_of_months = months;
        }
        else if (months.length === 2) {
            // if length of booking_months is 2, then we have a range of months
            // get all the months between start and end
            list_of_months = getMonthsBetween(months[0], months[1]);
        }
        else {
            console.error('Expected month values, got:', months.length);
            throw new Error('Failed to get booking_dates');
        }

        // it is important that the dates are formatted as "YYYY-MM-DD"
        // this ensures that the dates are captured correctly as the user selects them
        // and they are not converted to UTC. We need to capture the date the user sees/selects

        setBookingDates(list_of_months.map((month) => month.format("YYYY-MM-DD")));
        setBookingType("monthly");

        // trigger validation of the dates
        setTriggerBookingDatesValidation(true);

        // reset the other variables, user must reselect the desks
        ResetAfterDatesChange();

        // scroll to the desks
        scrollToDesks();

    };


    const SetBookingDatesValid = (isValid) => {

        setIsBookingDatesValid(isValid)

        // Reset trigger after validation so it will be called again if set true
        setTriggerBookingDatesValidation(false);
    };

    const personalInfoFormRef = useRef();

    const handleSubmitForPayment = async () => {

        // Prevent double-clicks
        if (isSubmitting) {
            return;
        }

        // Call validateForm in PersonalInfoForm
        if (personalInfoFormRef.current.validateForm()) {
            // Proceed with payment submission
            setIsSubmitting(true);

            const payload = {

                // personal info
                first_name: personalInfo.first_name.trim(),
                last_name: personalInfo.last_name.trim(),
                email: personalInfo.email.trim().toLowerCase(),

                // booking info
                // only one office is available for booking
                office_id: 1,
                booking_type: bookingType,
                booking_dates: finalDesks,
                renewal: isRenewal,
                // pass back the total price, to ensure the user has not tampered with the price
                // this total price will need to match the total price calculated on the server
                // it won't actually be stored in the database
                total_price: totalPrice,
                // discount code and amount if present
                discount_code: discountCode,
                discount_amount: discountAmount,
            };

            try {

                const bookingsURL = `${API_URL}/bookings`;

                const response = await apiFetch(bookingsURL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(`${data.error}`);
                }

                setGroupBookingID(data.group_booking_id);
                setIsProcessingPayment(true);

            } catch (error) {
                // catch any errors, i.e. ones not caught by the fetch call
                // and also the one thrown by the fetch call
                setApiErrorMessage(error.message);
                setIsErrorModalOpen(true);
                setIsSubmitting(false); // Re-enable button on error
            }


        } else {
            return;
        }
    };


    useEffect(() => {
        // the isProcessingPayment flag is set when the user has submitted the form
        // and the booking has been created in the database
        // if the flag is set, then we can proceed with the payment
        if (isProcessingPayment) {
            setIsShowPayPalPanel(true);
        }
        else {
            setIsShowPayPalPanel(false);
        }
    }, [isProcessingPayment]);

    useEffect(() => {
        if (isBookingDatesValid) {
            // Proceed with form submission or further processing
            // show the confirmation panel
            setIsShowAvailabilityPanel(true);
        }
        else {
            // hide the confirmation panel
            setIsShowAvailabilityPanel(false);
        }
    }, [isBookingDatesValid]);

    // Validate discount code from URL parameter on mount
    useEffect(() => {
        const discountCodeParam = searchParams.get('discount_code');

        if (discountCodeParam) {
            // Validate the discount code via API
            const validateDiscount = async () => {
                try {
                    const response = await apiFetch(`${API_URL}/discount/validate`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ discount_code: discountCodeParam }),
                    });

                    const data = await response.json();

                    if (response.ok && data.valid) {
                        setDiscountCode(discountCodeParam);
                        setDiscountPercentage(data.percentage);
                        console.log(`Discount code validated: ${data.percentage}% off`);
                    } else {
                        console.warn('Invalid discount code provided in URL');
                    }
                } catch (error) {
                    console.error('Error validating discount code:', error);
                }
            };

            validateDiscount();
        }
    }, [searchParams, API_URL]);

    // Check for renewal parameter from URL on mount
    useEffect(() => {
        const renewalParam = searchParams.get('renewal');
        if (renewalParam === 'true') {
            setIsRenewal(true);
            console.log('Renewal mode enabled - rolling hold bypassed');
        }
    }, [searchParams]);

    // Recalculate prices when discount or original price changes
    useEffect(() => {
        if (discountPercentage && originalPrice > 0) {
            const calculatedDiscountAmount = Math.round(originalPrice * (discountPercentage / 100.0) * 100) / 100;
            const finalPrice = Math.round((originalPrice - calculatedDiscountAmount) * 100) / 100;

            setDiscountAmount(calculatedDiscountAmount);
            setTotalPrice(finalPrice);
        } else if (originalPrice > 0) {
            // No discount, total price equals original price
            setTotalPrice(originalPrice);
            setDiscountAmount(null);
        }
    }, [discountPercentage, originalPrice]);

    useEffect(() => {

        // another scenario where we need to get the user to reselect the desks
        // if the final selected desks change, i.e. someone changes the desk choice
        // then we need to create a new booking in the database for this, and
        // get a new group_booking_id
        ResetAfterDeskSelectChange();

        scrollToSummary();

        // The dependency array is deliberately just `finalDesks`, and the lint rule is
        // silenced rather than obeyed.
        //
        // `ResetAfterDeskSelectChange` and `scrollToSummary` are declared inside this
        // component, so each render creates new function objects. Listing them here would
        // therefore re-run this effect on *every* render -- and this effect's job is to DELETE
        // the held booking. That is an endless loop of bookings being created and destroyed.
        //
        // "Run when the chosen desks change" is the intent, and `[finalDesks]` says exactly
        // that. Do not let a tidy-up add the functions back.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [finalDesks]);

    // If personal info changes after a booking has been created, delete the booking
    // so the user must re-submit with the corrected details.
    // If no booking exists yet, just reset the submitting state so the button is re-enabled.
    useEffect(() => {
        if (group_booking_id) {
            // Booking exists in database with old personal info - delete it
            ResetAfterPersonalInfoChange();
        } else if (isSubmitting) {
            // No booking yet, just reset the button state
            setIsSubmitting(false);
        }

        // `[personalInfo]` is deliberate and the lint rule is silenced rather than obeyed.
        // Adding `group_booking_id`, which is what it asks for, breaks booking entirely:
        //
        //   1. the customer submits, the backend creates the booking
        //   2. `setGroupBookingID` fires, so the id changes from null to a value
        //   3. that change re-runs this effect
        //   4. the id is now truthy, so `ResetAfterPersonalInfoChange` runs
        //   5. which DELETEs the booking created moments earlier, every single time
        //
        // `isSubmitting` is read here for the same reason and must stay out for the same
        // reason. `ResetAfterPersonalInfoChange` is redeclared each render, so listing it
        // would re-run this on every render.
        //
        // The intent is "the customer edited their details, so the held booking is stale".
        // Only `personalInfo` expresses that. Wrapping the resets in `useCallback` would
        // settle their identity but would not make `group_booking_id` safe to list, so it
        // does not get you out of needing this comment.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personalInfo]);




    return (
        <div>
            <div className="booking-selectors">

                <AvailabilityForm
                    handleBookingTypeButtonClick={handleBookingTypeButtonClick}
                    bookingType={bookingType}
                    bookingDates={bookingDates}
                    handleDailySelect={handleDailySelect}
                    handleMonthlySelect={handleMonthlySelect}
                    triggerBookingDatesValidation={triggerBookingDatesValidation}
                    SetBookingDatesValid={SetBookingDatesValid}
                    calendarRef={calendarRef}
                    scrollToCalendar={scrollToCalendar}
                />
            </div>
            <div className="booking-selections" ref={deskRef}>
                {isShowAvailabilityPanel && (
                    <AvailabilitySummary
                        bookingType={bookingType}
                        bookingDates={bookingDates}
                        setFinalDesks={setFinalDesks}
                        setTotalPrice={setOriginalPrice}
                        resetAfterTimeOutOrError={resetAfterTimeOutOrError}
                        isRenewal={isRenewal}
                        group_booking_id={group_booking_id}
                    />
                )}
            </div>
            <div>
                {Object.keys(finalDesks).length > 0 && (
                    <div className='selected-desks-container' ref={summaryRef}>
                        <h2>Your booking summary</h2>
                        <BookingConfirmationTable
                            finalDesks={finalDesks}
                            totalPrice={totalPrice}
                            bookingType={bookingType}
                            discountCode={discountCode}
                            discountPercentage={discountPercentage}
                            discountAmount={discountAmount}
                            originalPrice={originalPrice}
                        />
                        <h2>Personal details</h2>
                        <PersonalInfoForm
                            ref={personalInfoFormRef}
                            // Pass other props as needed
                            personalInfo={personalInfo}
                            setPersonalInfo={setPersonalInfo}
                        />
                        <div className='booking-submit-button-container'>
                            <button
                                type="submit"
                                onClick={() => handleSubmitForPayment()}
                                disabled={isSubmitting}>
                                {isSubmitting ? 'Processing...' : 'Make payment'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <div>
                {isShowPayPalPanel && group_booking_id && totalPrice > 0 && (
                    <div className='payment-container'>
                        <h2>Payment details</h2>
                        <BookingCountdown resetAfterTimeOutOrError={resetAfterTimeOutOrError} />
                        <div className='payment-details-container'>
                            <div className='payment-details'>
                                <p>
                                    You're nearly there. You've selected your desk days and entered your details—now it's time to complete your booking. We use PayPal to handle payments securely. You don't need a PayPal account—just choose the option that works best for you below.
                                </p>
                            </div>
                            <div className='payment-details'>
                                <PayPalButton
                                    amount={totalPrice}
                                    group_booking_id={group_booking_id}
                                    setIsErrorModalOpen={setIsErrorModalOpen}
                                    setApiErrorMessage={setApiErrorMessage}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {isErrorModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <p>
                            Sorry, there has been an issue processing your booking request. Please try again if it is a validation error.
                        </p>
                        <p>
                            If it relates to payment or door code access, please get in touch at <a href='help@thetypingrooms.co.uk'> help@thetypingrooms.co.uk</a> and we'll sort it manually.
                        </p>
                        <p className='error-code'>
                            {apiErrorMessage}
                        </p>
                        <button onClick={() => {
                            setIsErrorModalOpen(false);
                            resetAfterTimeOutOrError();
                        }}>
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingForm;
